/* PagingVersionControlFeed.js */

"use strict";

const Steve = require("@gluenetworks/steve_core").getInstance();
const {EventEmitter} = require("events");

const Uuid = require("uuid");
const sift = require("sift").default;
const _ = require("lodash");

class PagingVersionControlFeed extends EventEmitter {
    constructor(options) {
        super();

        // Retrieve Service Git
        this._gitService = Steve.di.getRegistered("$teve.services.Git").registeredObj;
        this._ConstructInstance = Steve.di.getRegistered(
            "$teve.entities.ConstructInstance"
        ).registeredObj;

        // Standard paging feed properties
        this._entityOrder = [];
        this._entityFields = {};
        this._userColumns = [];
        this._searchedEntityId = undefined;
        this._totalEntities = 0;

        // Standard paging feed properties from options
        this._userSession = _.get(options, "userSession");
        this._activeOrgId = _.get(
            options,
            "activeOrgId",
            _.get(options, ["userSession", "activeOrganization", "id"])
        );
        this._feedKey = _.get(options, "feedKey", "");
        this._filter = _.get(options, "filter", {});
        this.setColumnConfig(
            _.get(options, "columnConfig"),
            _.get(options, "additionalDataFields", [])
        );
        this._sort = _.get(options, "sort", {});
        if (typeof this._sort !== "object") {
            console.warn(this._sort, "Sort data error::Not an object");
            this._sort = {};
        }

        // Simulated change feed properties
        this._feed = {
            _changeFeedId: Uuid.v4(),
            _channelId: Uuid.v4(),
            _feedType: "MEMBER_OF",
            _cache: {},
            _status: "INITIALIZING",
        };
        Object.defineProperties(this._feed, {
            changeFeedId: {
                get() {
                    return this._changeFeedId;
                },
            },
            channelId: {
                get() {
                    return this._channelId;
                },
            },
            feedType: {
                get() {
                    return this._feedType;
                },
            },
            cache: {
                get() {
                    return this._cache;
                },
                set(value) {
                    this._cache = value;
                },
            },
            status: {
                get() {
                    return this._status;
                },
                set(value) {
                    this._status = value;
                },
            },
        });

        this.reestablish = _.debounce(this.reestablish.bind(this)); //for further consideration
    }

    /*******************************************/
    /* START INITIALIZATION METHODS            */
    /*******************************************/

    // Initialize _userColumns with grid's columnConfig and additionalDataFields
    setColumnConfig(config, additionalDataFields) {
        this._userColumns.length = 0;

        Object.keys(this._entityFields).forEach((key) => {
            delete this._entityFields[key];
        });
        _.forEach(config, (colDef, colName) => {
            const fieldName =
                config[colName].path ||
                (!_.isBoolean(colDef.entityColumn) && colDef.entityColumn) ||
                colName;
            this._entityFields[fieldName] = true;
            this._userColumns.push(
                _.chain(colDef)
                    .pick(["label", "dataType", "typeInfo", "valuePath", "options", "filterLabel"])
                    .set("name", fieldName)
                    .value()
            );
        });
        additionalDataFields.forEach((fieldName) => (this._entityFields[fieldName] = true));
    }

    /*******************************************/
    /* END INITIALIZATION METHODS             */
    /* START GETTERS AND SETTERS               */
    /*******************************************/

    get feed() {
        return this._feed;
    }

    get cache() {
        return this.feed.cache;
    }

    getPageEntities() {
        return this._feed.cache;
    }

    get entityOrder() {
        return this._entityOrder;
    }

    get activePage() {
        return 1;
    }

    get firstPage() {
        return 1;
    }

    get lastPage() {
        return 1;
    }

    get totalEntities() {
        return this._totalEntities;
    }

    get pageSize() {
        return this._totalEntities;
    }

    getSort() {
        return this._sort;
    }

    get sort() {
        return this._sort;
    }

    get _lowWaterMark() {
        return this._totalEntities;
    }

    get _highWaterMark() {
        return 3 * this._totalEntities;
    }

    get _firstPage() {
        return 1;
    }

    get _activePage() {
        return 1;
    }

    get _initialPage() {
        return {skip: 0, limit: this._totalEntities};
    }

    get _currentPageInfo() {
        return {
            total: this._totalEntities,
            before: 0,
            on: this._totalEntities,
            after: 0,
        };
    }

    /*******************************************/
    /* END GETTERS AND SETTERS                 */
    /* START PUBLIC METHODS                    */
    /*******************************************/

    whenFeedReady() {
        return this._getFeed().then(() => {
            this.emit("FeedInitialized", this);
            return this;
        });
    }

    async reestablish() {
        this._entityOrder = [];
        await this._feedReestablish();
        this.emit("RefreshView", this);
        this.emit("RefreshLinesNumber", this);
        return this;
    }

    feedUpdate() {
        return this._feedReestablish();
    }

    getFilterData() {
        return this._filter;
    }

    setFilter(query, silent = false) {
        query = query || {};

        const is$userQuery = Object(this._filter).hasOwnProperty("$userQuery");
        const userFilter = is$userQuery ? this._filter.$userQuery : this._filter;
        if (_.isEmpty(query) && _.isEmpty(userFilter)) {
            return;
        }

        this._filter = is$userQuery ? Object.assign({}, this._filter, {$userQuery: query}) : query;
        if (!silent) {
            this.reestablish();
        }
    }

    setSort(sortData, silent) {
        if (!_.isEqual(this._sort, sortData)) {
            if (typeof sortData !== "object") {
                console.warn(sortData, "SetSort::Sort data error::Not an object");
                sortData = this._sort;
            }
            const sortingKeys = Object.keys(sortData);
            for (let i = 0; i < sortingKeys.length; i++) {
                if (!(sortData[sortingKeys[i]] === 1 || sortData[sortingKeys[i]] === -1)) {
                    console.warn(this._sort, "SetSort::Sort data error::value is not 1 or -1");
                    sortData = this._sort;
                    break;
                }
            }
            this._sort = sortData;
            if (!silent) {
                this.reestablish();
            }
        } else {
            this.emit("RefreshView", this);
        }
    }

    setActivePage() {}

    isEmpty() {
        return this._totalEntities === 0;
    }

    // Return _userColumns with initialized dataType
    getColumnsData() {
        return this._userColumns.map((userColData) =>
            Object.assign({}, userColData, {
                dataType: userColData.dataType
                    ? String(userColData.dataType).toLowerCase()
                    : "default",
            })
        );
    }

    getRangeSelection(endId, startId) {
        if (!this._feed || !this._feed.cache) {
            return {};
        }

        const keys = Object.keys(this._feed.cache);
        const startIndex = keys.indexOf(startId);
        const endIndex = keys.indexOf(endId);

        if (startIndex === -1 || endIndex === -1) {
            return {}; // one or both IDs not found
        }

        const [from, to] = startIndex <= endIndex ? [startIndex, endIndex] : [endIndex, startIndex];

        return keys.slice(from, to + 1);
    }

    getEntitiesValueById(ids, field) {
        let values = [];

        ids.forEach((id) => {
            if (this._feed.cache[id]) {
                values.push(_.get(this._feed.cache[id], field));
            } else {
                console.error(
                    `PagingVersionControlFeed: getEntitiesValueById failed to retrieve ID: ${id}`
                );
            }
        });

        return values;
    }

    getEntitiesValueByIdAsProm(ids, field) {
        return Promise.resolve(this.getEntitiesValueById(ids, field));
    }

    getEntitiesNameById(ids) {
        return this.getEntitiesValueById(ids, "name");
    }

    /* Not used by Version Control
    getEntitiesByIdAsProm(ids = []) {
        let entities = [];

        ids.forEach((id) => {
            if (this._feed.cache[id]) {
                entities.push(_.get(this._feed.cache, id));
            } else {
                console.error(
                    `PagingVersionControlFeed: getEntitiesByIdAsProm failed to retrieve ID: ${id}`
                );
            }
        });
        return Promise.resolve(entities);
    }
    */

    getEntityById(id) {
        return this._feed.cache[id];
    }

    getEntitiesById(ids) {
        let entities = [];
        ids.forEach((id) => {
            if (this._feed.cache[id]) {
                entities.push(this._feed.cache[id]);
            } else {
                console.error(
                    `PagingVersionControlFeed: getEntitiesById failed to retrieve ID: ${id}`
                );
            }
        });
        return entities;
    }

    wasPreviouslyInitialized() {
        return true;
    }

    shutdown() {
        return this._feedShutDown().then(() => {
            this._gitService = undefined;
            this._ConstructInstance = undefined;
            this._entityOrder = undefined;
            this._searchedEntityId = undefined;
            this._totalEntities = undefined;
            this._activeOrgId = undefined;
            this._feedKey = undefined;
            this._sort = undefined;
            this._filter = undefined;
            this._userSession = undefined;
            this._entityFields = undefined;
            this._userColumns = undefined;
        });
    }

    async fetchAllEntityIds() {
        return Promise.resolve(this._entityOrder);
    }

    /*******************************************/
    /* END PUBLIC METHODS                      */
    /* START PRIVATE SUPPORT METHODS           */
    /*******************************************/

    _fetchAllEntityIds() {
        const cache = this._feed._cache || {};
        const entities = Object.values(cache);
        const entityIds = entities.sort((a, b) => a.pagePos - b.pagePos).map((item) => item.id);

        return entityIds;
    }

    _feedShutDown() {
        if (this._feed) {
            this._feed = undefined;
        }
        return Promise.resolve();
    }

    _feedReestablish() {
        return this._getFeed().then(() => {
            this.emit("FeedReestablished", this);
            this.emit("RefreshLinesNumber", this);
            return this;
        });
    }

    _getFeedIdsForSelection() {
        console.warn("_getFeedIdsForSelection not defined for version control feed");
    }

    /*******************************************/
    /* END PRIVATE SUPPORT METHODS             */
    /* START SIMULATED FEED METHODS            */
    /*******************************************/

    async _getFeed() {
        try {
            const response = await this._gitService.list({orgId: this._activeOrgId});
            const files = _.get(response, "files", []);

            this._feed._cache = {};

            const CATEGORY_CONSTRUCT = "construct";
            const CATEGORY_ENTITY = "entity";
            const CATEGORY_ENTITY_TYPE_MAP = {
                ConfigAuditPolicy: "Config Audit Policy",
                WorkflowType: "RPA Workflow",
            };
            const CATEGORY_ENTITY_TYPES = Object.keys(CATEGORY_ENTITY_TYPE_MAP);

            const pathInfoMap = new Map();
            const entityNamesByType = new Map();
            CATEGORY_ENTITY_TYPES.forEach((type) => entityNamesByType.set(type, new Set()));
            const constructDescriptors = [];

            // Prepare entityNames and constructDescriptors
            files.forEach((file) => {
                try {
                    const pathInfo = this._extractPathInfo(file.path);

                    if (!pathInfo) {
                        return;
                    }

                    // Cache for later
                    pathInfoMap.set(file.path, pathInfo);

                    const {category, type, typeName, instanceName} = pathInfo;

                    if (category === CATEGORY_ENTITY && instanceName) {
                        if (CATEGORY_ENTITY_TYPES.includes(type)) {
                            entityNamesByType.get(type).add(instanceName);
                        }
                    } else if (
                        category === CATEGORY_CONSTRUCT &&
                        type &&
                        typeName &&
                        instanceName
                    ) {
                        constructDescriptors.push({
                            construct: type,
                            typeName: typeName,
                            instanceName: instanceName,
                        });
                    }
                } catch (error) {
                    console.error(
                        `PagingVersionControlFeed._getFeed(): Failed to pre-process file ${file.path}`,
                        error
                    );
                }
            });

            // Build promises for each entity type
            const entityPromises = CATEGORY_ENTITY_TYPES.map((type) => {
                const namesSet = entityNamesByType.get(type);
                return namesSet.size > 0
                    ? this._retrieveEntities(type, Array.from(namesSet))
                    : Promise.resolve([]);
            });

            // Run both entities and constructs async load calls in parallel
            const [entitiesArrays, constructs] = await Promise.all([
                Promise.all(entityPromises),
                constructDescriptors.length > 0
                    ? this._retrieveConstructs(constructDescriptors)
                    : Promise.resolve([]),
            ]);

            // Flatten all retrieved entities
            const entities = [].concat(...entitiesArrays);

            // Build maps
            const entityMap = new Map();
            for (let i = 0; i < entities.length; i++) {
                const entity = entities[i];
                entityMap.set(entity.name, entity);
            }

            const constructMap = new Map();
            for (let i = 0; i < constructs.length; i++) {
                const construct = constructs[i];
                const type =
                    _.get(construct, "meta.construct") || _.get(construct, "properties.construct");
                const typeName = _.get(construct, "meta.type.name");
                const instanceName =
                    _.get(construct, "meta.name") || _.get(construct, "properties.name");
                if (type && typeName && instanceName) {
                    const key = this._constructKey(type, typeName, instanceName);
                    constructMap.set(key, construct);
                }
            }

            // Iterate through files to populate feed with matching entities or constructs
            for (let f = 0; f < files.length; f++) {
                const file = files[f];

                try {
                    const pagePos = f;
                    const pathInfo = pathInfoMap.get(file.path);

                    if (!pathInfo) {
                        continue;
                    }

                    const {category, type, typeName, instanceName, featureType, featureInstance} =
                        pathInfo;

                    let id, description, displayTypeName, updatedOn, updatedBy;
                    let visibility = "";

                    if (category === CATEGORY_ENTITY) {
                        const entity = entityMap.get(instanceName);
                        if (!entity) {
                            continue;
                        }

                        id = entity.id;
                        if (entity.private === true) {
                            visibility = "Private";
                        } else if (entity.private === false) {
                            visibility = "Public";
                        }
                        description = entity.description;
                        updatedOn = entity.updatedOn;
                        updatedBy = entity.updatedBy;
                    } else if (category === CATEGORY_CONSTRUCT) {
                        const key = this._constructKey(type, typeName, instanceName);
                        const construct = constructMap.get(key);

                        if (!construct || !construct.meta || !construct.properties) {
                            continue;
                        }

                        const meta = construct.meta;
                        const properties = construct.properties;

                        if (featureType) {
                            id = meta.instanceId + "#" + featureType + "-" + featureInstance;
                        } else {
                            id = meta.instanceId;
                            description = meta.description;
                        }

                        if (meta.type && meta.type.displayName) {
                            displayTypeName = meta.type.displayName;
                        } else {
                            displayTypeName = _.startCase(typeName);
                        }

                        updatedOn = properties.updatedOn;
                        updatedBy = properties.updatedBy;
                    } else {
                        throw new Error(
                            `PagingVersionControlFeed._getFeed(): Unknown configuration asset category '${category}'`
                        );
                    }

                    const commonProperties = {
                        category: category,
                        type: type,
                        displayType: CATEGORY_ENTITY_TYPE_MAP[type] || _.startCase(type),
                        displayTypeName: displayTypeName,
                        featureType: featureType,
                        displayFeatureType: _.startCase(featureType),
                        featureInstance: featureInstance,
                        displayFeatureInstance: _.startCase(featureInstance),
                        typeName: typeName,
                        name: instanceName,
                        description: description,
                        visibility: visibility,
                        updatedOn: updatedOn,
                        updatedBy: updatedBy,
                    };

                    const cacheEntry = {
                        id: id,
                        getId: function () {
                            return "" + id;
                        },
                        pagePos: pagePos,
                        _isStatic: false,
                        _pendingPatches: null,
                        _pendingRelationships: [],
                        _synchronized: true,
                        _tenantId: this._activeOrgId,
                        _type: "",
                        on: function () {
                            return null;
                        },
                        removeListener: function () {
                            return null;
                        },
                        _pristineProperties: {},
                        getProperty: function (propName) {
                            if (
                                typeof propName === "string" &&
                                propName[0] !== "_" &&
                                typeof this[propName] !== "function"
                            ) {
                                return this[propName];
                            }
                            return undefined;
                        },
                        setProperty: function (propName, value) {
                            if (
                                typeof propName === "string" &&
                                propName[0] !== "_" &&
                                typeof this[propName] !== "function"
                            ) {
                                this[propName] = value;
                            }
                        },
                    };

                    Object.assign(cacheEntry, commonProperties);
                    cacheEntry._pristineProperties = Object.assign({}, commonProperties);

                    const proxiedCacheEntry = new Proxy(cacheEntry, {
                        get(target, prop, receiver) {
                            if (
                                prop !== "getProperty" &&
                                typeof prop === "string" &&
                                prop.startsWith("get") &&
                                prop.length > 3
                            ) {
                                const rawProp = prop.slice(3);
                                const key = rawProp.charAt(0).toLowerCase() + rawProp.slice(1);
                                return () => target.getProperty(key);
                            }
                            return Reflect.get(target, prop, receiver);
                        },
                    });

                    this._feed._cache[id] = proxiedCacheEntry;
                } catch (error) {
                    console.error(
                        `PagingVersionControlFeed._getFeed(): Failed to process file ${file.path}`,
                        error
                    );
                }
            }

            if (!_.isEmpty(this._sort)) {
                this._feed._cache = this._sortAndFilterObjectOfObjects(
                    this._feed._cache,
                    this._sort,
                    this._filter
                );
            }

            this._totalEntities = Object.keys(this._feed._cache).length;
            this._feed._status = "INITIALIZED";
            this._feed.status = "INITIALIZED";

            this._feed.cache = this._feed._cache;
            this._entityOrder = this._fetchAllEntityIds();

            return this._feed;
        } catch (error) {
            console.error("PagingVersionControlFeed._getFeed(): Error fetching feed:", error);
        }
    }

    _extractPathInfo(filePath) {
        const validCategories = ["entity", "construct"];
        const validTypes = [
            "ConfigAuditPolicy",
            "WorkflowType",
            "node",
            "feature",
            "global",
            "domain",
        ];

        const removeExtension = (name) => {
            const parts = name.split(".");
            return parts.length > 1 ? parts.slice(0, -1).join(".") : name;
        };

        const pathParts = filePath.split("/");

        const categoryIndex = pathParts.findIndex((part) => validCategories.includes(part));

        if (categoryIndex === -1) {
            return null; // No valid category found
        }

        const category = pathParts[categoryIndex]; // Entity or Construct

        // Validate category - must be "Entity" or "Construct"
        if (!validCategories.includes(category)) {
            return null; // Invalid category, discard
        }

        const type = pathParts[categoryIndex + 1]; // Entity Type or Construct Type

        // Validate type - must be one of the specified values
        if (!validTypes.includes(type)) {
            return null; // Invalid type, discard
        }

        if (category === "construct") {
            const typeName = pathParts[categoryIndex + 2];
            const instanceNameWithExtension = pathParts[categoryIndex + 3];
            const instanceName = removeExtension(instanceNameWithExtension);
            const featureType = pathParts[categoryIndex + 5];

            const featureInstanceWithExtension = pathParts[categoryIndex + 6];
            const featureInstance = featureInstanceWithExtension
                ? removeExtension(featureInstanceWithExtension) || undefined
                : undefined;

            // Validate name - must be not empty
            if (!instanceName) {
                return null; // Invalid fileName
            }

            return {
                category,
                type,
                typeName,
                instanceName,
                featureType,
                featureInstance,
            };
        } else {
            const instanceNameWithExtension = pathParts[categoryIndex + 2];
            const instanceName = removeExtension(instanceNameWithExtension);

            // Validate name - must be not empty
            if (!instanceName) {
                return null; // Invalid fileName
            }

            return {
                category,
                type,
                typeName: undefined,
                instanceName,
                featureType: undefined,
                featureInstance: undefined,
            };
        }
    }

    async _retrieveEntities(entityType, entityNames) {
        if (!entityType || !Array.isArray(entityNames) || entityNames.length === 0) {
            throw new Error("Invalid arguments: entityType, and entityNames are required.");
        }

        const registeredClass = Steve.di.getRegistered("$teve.entities." + entityType);

        const query = {
            name: {$in: entityNames},
        };

        if (registeredClass) {
            const registeredObj = registeredClass.registeredObj;
            if (registeredObj) {
                return await registeredObj.loadByQuery(this._activeOrgId, entityType, query);
            }
        }

        throw new Error("Invalid arguments: unknown entityType.");
    }

    async _retrieveConstructs(constructDescriptors) {
        if (!Array.isArray(constructDescriptors) || constructDescriptors.length === 0) {
            throw new Error("Invalid argument: constructDescriptors must be a non-empty array.");
        }

        const innards = ["meta", "properties"];

        return await this._ConstructInstance.loadConstructInstances(
            constructDescriptors,
            this._activeOrgId,
            innards
        );
    }

    _sortAndFilterObjectOfObjects(obj, sortCriteria, filterCriteria) {
        let entries = Object.values(obj);
        const result = {};
        let pagePos = 0;

        entries.sort((objA, objB) => {
            // Iterate through sort criteria
            for (const [property, direction] of Object.entries(sortCriteria)) {
                if (objA[property] !== objB[property]) {
                    // Handle string comparison case-insensitively
                    if (typeof objA[property] === "string" && typeof objB[property] === "string") {
                        return (
                            (objA[property].toLowerCase() < objB[property].toLowerCase() ? -1 : 1) *
                            direction
                        );
                    } else {
                        // For non-string values, compare normally
                        return (objA[property] < objB[property] ? -1 : 1) * direction;
                    }
                }
            }
            return 0;
        });

        if (!_.isEmpty(filterCriteria)) {
            entries = entries.filter(sift(filterCriteria));
        }

        // Convert sorted entries back to object
        entries.forEach((entry) => {
            result[entry.id] = entry;
            // Re-index `pagePos`, which is used by `entity_grid_gl` for sorting
            result[entry.id].pagePos = pagePos++;
        });

        return result;
    }

    // Utility function to build construct key
    _constructKey(construct, typeName, instanceName) {
        return `${construct}::${typeName}::${instanceName}`;
    }

    /*******************************************/
    /* END SIMULATED FEED METHODS              */
    /*******************************************/
}

module.exports = PagingVersionControlFeed;
